/* eslint-disable react/prop-types */
import React, { useMemo, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Field, formValueSelector } from 'redux-form'
import { lpForm } from 'lp-form'
import {
  ReadOnlyCellInput,
  NestedAttributesFieldArray,
  SectionHeader,
} from 'components'
import { noop, groupBy, size } from 'lodash'
import * as Types from 'types'
import { pluralize } from 'utils'
import { AddPartnerPartnershipModal, CopyPartnershipModal } from '../components'
import * as flashActions from 'redux-flash'
import { SUCCESS_FLASH_MODAL_CLOSE_DELAY } from 'config'
import classnames from 'classnames'
import { ArrowUpIcon, ArrowDownIcon } from 'components/icons'
import LoadingSpinner from 'components/loading-spinner'
import LoadingContainer from 'components/loading-container'

const propTypes = {
  change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  flashSuccessMessage: PropTypes.func.isRequired,
  previousPartnerships: PropTypes.arrayOf(Types.partnership),
  partnerOptions: PropTypes.arrayOf(Types.searchSuggestion),
  partnerships: PropTypes.arrayOf(Types.partnerPartnershipType),
  schoolYears: PropTypes.arrayOf(Types.schoolYear).isRequired,
  submitting: PropTypes.bool.isRequired,
  submitFailed: PropTypes.bool.isRequired,
  visibleSchoolYearId: PropTypes.number.isRequired,
  setShowAddPartnershipModal: PropTypes.func.isRequired,
  showAddPartnershipModal: PropTypes.bool.isRequired,
  updateArtist: PropTypes.func.isRequired,
}

const defaultProps = {
  error: '',
  partnerships: [],
  disabled: false,
  previousPartnerships: [],
}

function useActivePartnerCallback(partnerOptions) {
  const activePartnerMap = useMemo(
    () => groupBy(partnerOptions, 'id'),
    [partnerOptions]
  )
  return useCallback(
    (partnerId) => {
      return !!activePartnerMap[partnerId]
    },
    [activePartnerMap]
  )
}

function PartnerRow({ fields, index, handleSubmit, setLoading }) {
  const [showPrograms, setShowPrograms] = useState(false)
  const field = fields.name ? `${fields.name}[${index}]` : `${index}`
  const partner = fields.get(index)

  const handleRemove = () => {
    setLoading(true)
    try {
      fields.destroy(index)

      // Submit the form to persist changes
      setTimeout(() => {
        handleSubmit()
        setLoading(false)
      }, 0)
    } catch (error) {
      setLoading(false)
    }
  }

  return (
    <React.Fragment>
      <tr role="group" aria-label={`Partner ${partner.partnerName}`}>
        <th scope="row" className="table-name">
          <div className="table-name-wrapper">
            <button
              type="button"
              className="remove-row action-button"
              onClick={handleRemove}
              aria-label={`Remove ${
                partner.organizationName || 'organization'
              } partnership`}
            >
              <span aria-hidden="true">×</span>
            </button>
            <Field
              name={field + '.organizationName'}
              label={false}
              component={ReadOnlyCellInput}
            />
            {partner.state === 'pending_approval' && (
              <div className="pending-approval">Pending</div>
            )}
          </div>
        </th>
        <td className="table-actions">
          <div className="table-actions-wrapper">
            {partner.partnerPrograms?.length > 0 && (
              <>
                <button
                  type="button"
                  className={classnames('action-button', {
                    'visually-hidden': !showPrograms,
                  })}
                  onClick={() => setShowPrograms(false)}
                  aria-label="Hide program list"
                >
                  <ArrowUpIcon />
                </button>
                <button
                  type="button"
                  className={classnames('action-button', {
                    'visually-hidden': showPrograms,
                  })}
                  onClick={() => setShowPrograms(true)}
                  aria-label="Show program list"
                >
                  <ArrowDownIcon />
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
      <tr className="table-programs">
        <td colSpan="3">
          <ul className={classnames({ 'visually-hidden': !showPrograms })}>
            {partner.partnerPrograms?.map((program, programIndex) => (
              <li
                key={`partner-${index}-program-${programIndex}-${
                  program.programId || program.name
                }`}
              >
                {program.name}
              </li>
            ))}
          </ul>
        </td>
      </tr>
    </React.Fragment>
  )
}

function PartnerFields({
  fields,
  handleSubmit,
  setLoading,
  change,
  partnerships,
}) {
  return (
    <tbody>
      {fields.map((_, i) => {
        return (
          <PartnerRow
            key={`partner-${i}`}
            index={i}
            fields={fields}
            handleSubmit={handleSubmit}
            setLoading={setLoading}
            change={change}
            partnerships={partnerships}
          />
        )
      })}
    </tbody>
  )
}

function PartnerPartnershipTableForm({
  change,
  disabled,
  error,
  flashSuccessMessage,
  handleSubmit,
  schoolYears,
  visibleSchoolYearId,
  partnerships,
  partnerOptions,
  previousPartnerships,
  submitFailed,
  setShowAddPartnershipModal,
  showAddPartnershipModal,
  updateArtist,
  artistId,
}) {
  const isActivePartner = useActivePartnerCallback(partnerOptions)
  const [loading, setLoading] = useState(false)

  const visibleYear = useMemo(() => {
    return schoolYears.find(({ id }) => id === visibleSchoolYearId)
  }, [schoolYears, visibleSchoolYearId])

  const remainingPreviousPartnerships = useMemo(() => {
    const eligiblePartnerships = previousPartnerships.filter((partnership) => {
      return (
        isActivePartner(partnership.organizationId) &&
        !partnerships.some(
          ({ organizationId }) => organizationId === partnership.organizationId
        )
      )
    })

    return eligiblePartnerships
  }, [previousPartnerships.length, partnerships.length, isActivePartner])

  const remainingPartnerOptions = useMemo(() => {
    return partnerOptions.filter((partner) => {
      return !partnerships.some(
        ({ organizationId }) => organizationId === partner.id
      )
    })
  }, [partnerOptions, partnerships])

  const [showCopyPartnershipModal, setShowCopyPartnershipModal] =
    useState(false)

  return (
    <>
      <SectionHeader>
        <h2>What organizations do you work with?</h2>
        {!disabled && (
          <div className="button-area many-buttons">
            <button
              type="button"
              onClick={() => setShowAddPartnershipModal(true)}
              className="button-primary-outline"
            >
              Add a new Organization
            </button>
            <button
              type="button"
              onClick={() => setShowCopyPartnershipModal(true)}
              className="button-primary-outline"
            >
              Copy Organization from other school years
            </button>
          </div>
        )}
        <p>
          Note: Organizations will need to approve the connection before it
          shows on the map
        </p>

        <div className="portal partner-artists">
          <LoadingContainer isLoading={loading}>
            {loading && <LoadingSpinner />}
            <form onSubmit={disabled ? noop : handleSubmit} disabled={disabled}>
              <table>
                <thead>
                  <tr>
                    <th scope="col">Organization Name</th>
                    {partnerships && partnerships.length > 0 && (
                      <th scope="col" aria-label="Actions"></th>
                    )}
                  </tr>
                </thead>
                <NestedAttributesFieldArray
                  name="partnerships"
                  component={PartnerFields}
                  handleSubmit={handleSubmit}
                  setLoading={setLoading}
                  disabled={disabled}
                  change={change}
                  partnerships={partnerships}
                />
              </table>
              {submitFailed && error && (
                <div className="partnership-table-errors">
                  <p className="error-message">{error}</p>
                </div>
              )}
            </form>
          </LoadingContainer>
        </div>
      </SectionHeader>
      {showAddPartnershipModal && (
        <AddPartnerPartnershipModal
          onClose={() => setShowAddPartnershipModal(false)}
          onSubmit={async ({ partner, programs, schoolIds }) => {
            setLoading(true)

            // If schoolIds are provided, create school-specific partnerships using updateArtist
            if (schoolIds && schoolIds.length > 0) {
              try {
                // Create partnerships for each selected school
                const schoolPartnerships = schoolIds.map((schoolId) => ({
                  schoolId: schoolId,
                }))

                // Update the artist with the new school partnerships
                await updateArtist(artistId, visibleSchoolYearId, {
                  partnerships: schoolPartnerships,
                })
              } catch (error) {
                setLoading(false)
                // Re-throw the error so it can be handled by the form's error system
                throw new Error(
                  `Failed to create school partnerships: ${
                    error.message || 'Unknown error'
                  }`
                )
              }
            }

            // Create the organization partnership (existing behavior)
            const newPartnership = {
              organizationId: partner.id,
              organizationName: partner.name,
              partnerPrograms: programs.map((program) => {
                return {
                  programId: program.id,
                  name: program.name,
                }
              }),
            }
            change('partnerships', [...partnerships, newPartnership])

            // Submit the form after a brief delay to ensure state is updated
            setTimeout(() => {
              handleSubmit()
              setLoading(false)
            }, 0)
            return {
              ...newPartnership,
              hasSchoolPartnerships: schoolIds && schoolIds.length > 0,
            }
          }}
          onSubmitSuccess={({ organizationName, hasSchoolPartnerships }) => {
            setShowAddPartnershipModal(false)
            const schoolPartnershipMessage = hasSchoolPartnerships
              ? ` and school partnerships created`
              : ''
            setTimeout(
              () =>
                flashSuccessMessage(
                  `Partnership with ${organizationName} added for the ${visibleYear.number} school year${schoolPartnershipMessage}`
                ),
              SUCCESS_FLASH_MODAL_CLOSE_DELAY
            )
          }}
          partnerOptions={remainingPartnerOptions}
          visibleSchoolYearId={visibleSchoolYearId}
        />
      )}

      {showCopyPartnershipModal && (
        <CopyPartnershipModal
          onClose={() => setShowCopyPartnershipModal(false)}
          onSubmit={async ({ partnerships: newPartnerships }) => {
            setLoading(true)
            change('partnerships', [...partnerships, ...newPartnerships])
            // Submit the form after a brief delay to ensure state is updated
            setTimeout(() => {
              handleSubmit()
              setLoading(false)
            }, 0)
            return newPartnerships
          }}
          onSubmitSuccess={(partnerships) => {
            setShowCopyPartnershipModal(false)
            const numNewPartnerships = size(partnerships)
            setTimeout(
              () =>
                flashSuccessMessage(
                  `${numNewPartnerships} ${pluralize(
                    'partnership',
                    numNewPartnerships
                  )} copied over for the ${visibleYear.number} school year`
                ),
              SUCCESS_FLASH_MODAL_CLOSE_DELAY
            )
          }}
          partnerships={remainingPreviousPartnerships}
          schoolYears={schoolYears}
        />
      )}
    </>
  )
}

PartnerPartnershipTableForm.propTypes = propTypes
PartnerPartnershipTableForm.defaultProps = defaultProps

function mapStateToProps(state, { name }) {
  return {
    partnerships: formValueSelector(name)(state, 'partnerships'),
  }
}

const mapDispatchToProps = {
  flashSuccessMessage: flashActions.flashSuccessMessage,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  lpForm({
    submitFilters: {
      allow: ['id', 'partnerships'],
    },
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    destroyOnUnmount: false, // allow switching between year tabs without losing in-progress data
  })
)(PartnerPartnershipTableForm)
